import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/auth.store';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NEGOCIO } from '@/lib/negocio';

const schema = z.object({
  email: z.string().min(1, 'El email es obligatorio').email('Email inválido'),
  password: z.string().min(1, 'La contraseña es obligatoria'),
});
type FormValues = z.infer<typeof schema>;

export default function Login() {
  const { login, isLoading } = useAuth();
  const accessToken = useAuthStore((s) => s.accessToken);
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { email: '', password: '' } });

  if (accessToken) return <Navigate to={from} replace />;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login(values.email, values.password, from);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'No se pudo iniciar sesión';
      setError('root', { message });
    }
  });

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-100 p-4 dark:bg-[#161616]">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/silva-notion-isotipo.png" alt="Silva Notion" className="h-14 w-14 object-contain" />
          <h1 className="mt-4 text-[26px] font-bold leading-8 tracking-[-0.01em] text-slate-900 dark:text-slate-100">{NEGOCIO}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Tus chats, tus clientes y tu equipo, en un solo lugar.
          </p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_0.5px_rgb(0_0_0/0.12),0_12px_32px_rgb(0_0_0/0.10)] dark:bg-slate-950 dark:shadow-[0_0_0_0.5px_rgb(255_255_255/0.12),0_12px_32px_rgb(0_0_0/0.5)]">
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="usted@empresa.com"
              error={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Contraseña"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              error={errors.password?.message}
              {...register('password')}
            />

            {errors.root && (
              <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {errors.root.message}
              </div>
            )}

            <Button type="submit" size="lg" className="w-full" loading={isLoading}>
              Iniciar sesión
            </Button>

            <p className="text-center text-xs">
              <Link to="/forgot-password" className="inline-flex min-h-11 items-center text-acento-texto hover:underline lateral:min-h-0">
                ¿Olvidó su contraseña?
              </Link>
            </p>
          </form>
        </div>


        <p className="mt-6 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} Silva Notion EIRL
        </p>
      </div>
    </div>
  );
}
